import { Icon } from '@aibees/shared/components';
import {
  alertClasses,
  alertTitleClasses,
  type AlertProps,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';

const COLORS = ['default', 'info', 'success', 'warning', 'error'] as const;

declare module '@mui/material/Alert' {
  interface AlertPropsSeverityOverrides {
    default: true;
  }
  interface AlertPropsColorOverrides {
    default: true;
  }
}

export default function Alert(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyles = (ownerState: AlertProps) => {
    const standardVariant = ownerState.variant === 'standard';

    const filledVariant = ownerState.variant === 'filled';

    const outlinedVariant = ownerState.variant === 'outlined';

    const defaultStyle = {
      padding: theme.spacing(0, 1.5),
      borderRadius: theme.spacing(0.5),
      [`& .${alertClasses.message}`]: {
        width: '100%',
        flex: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        ...theme.typography.body3,
      },
      [`&.${alertClasses.root}`]: {},

      [`& > .${alertClasses.icon}`]: {
        marginRight: theme.spacing(1),
      },
      [`& .${alertClasses.action}`]: {
        marginRight: 0,
        alignItems: 'center!important',
        padding: theme.spacing(0, 0, 0, 2),
      },
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.severity === color && {
        // STANDARD
        ...(standardVariant && {
          backgroundColor: theme.palette[color][50],
          border: `solid 1px ${theme.palette[color][200]}`,
          color: theme.palette[color][700],
          [`& > .${alertTitleClasses.root}`]: {
            color: theme.palette[color][700],
            marginBottom: theme.spacing(0.5),
          },
          [`& .${alertClasses.icon}`]: {
            color: theme.palette[color][700],
          },
        }),
        // FILLED
        ...(filledVariant && {
          color: theme.palette[color].contrastText,
          backgroundColor:
            color === 'error'
              ? theme.palette[color][700]
              : theme.palette[color][500],
          [`& > .${alertClasses.icon}`]: {
            // backgroundColor: 'transparent!important',
          },
          [`& > .${alertClasses.icon} > .icon`]: {
            color: theme.palette[color].contrastText,
          },
        }),
        // OUTLINED
        ...(outlinedVariant && {
          color: theme.palette[color][isLight ? 'dark' : 'light'],
          backgroundColor: theme.palette.common.white,
          border: `solid 1px ${alpha(theme.palette[color].main, 0.16)}`,
          [`& .${alertClasses.icon}`]: {
            color: theme.palette[color].main,
          },
        }),
      }),
    }));

    return [defaultStyle, ...colorStyle];
  };

  return {
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          error: <Icon name="info-circle-outline" size={20} />,
          info: <Icon name="info-circle-outline" size={20} />,
          success: <Icon name="check-circle-outline" size={20} />,
          warning: <Icon name="alert" size={20} />,
          default: <Icon name="info-circle-outline" size={20} />,
        },
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: AlertProps }) =>
          rootStyles(ownerState),
        icon: {
          opacity: 1,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          ...theme.typography.body3,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
      },
    },
  };
}
